import { ResolvedComponentsConfiguration } from "@zoovu/runner-browser-api";
import globals from "./global-variables";
import stylesConfig from "../styles.config";
import mediaQuery from "../abstract/media-query";

const buttonBackgroundColors = {
  previous: {
    default: globals.colors.transparent,
    hover: globals.colors.transparent,
    active: globals.colors.transparent,
  },
  next: {
    default: stylesConfig.colors.primaryAccentColorVariants.default,
    hover: stylesConfig.colors.primary.spectrum500,
    active: stylesConfig.colors.primary.spectrum500,
  },
  startOver: {
    default: globals.colors.transparent,
    hover: globals.colors.transparent,
    active: globals.colors.transparent,
  },
};

const previousButtonTextColor = stylesConfig.colors.primaryAccentColorVariants.default;
const nextButtonTextColor = stylesConfig.colors.secondaryFontColor;
const paginationNextButtonTextColor = globals.colors.contrastDark;
const nextButtonBorderColor = stylesConfig.colors.primaryAccentColorVariants.default;
const previousButtonBorderColor = stylesConfig.colors.primaryAccentColorVariants.default;

const paginationButtonBackgroundColors = {
  hover: stylesConfig.colors.primaryAccentColorVariants.default,
  active: stylesConfig.colors.primaryAccentColorVariants.dark,
};

const paginationButtonBorderColor = {
  default: stylesConfig.colors.primaryAccentColorVariants.default, // TODO ADJUST
  hover: stylesConfig.colors.primaryAccentColorVariants.default, // TODO ADJUST
  active: stylesConfig.colors.primaryAccentColorVariants.dark, // TODO ADJUST
  nextback: globals.colors.gray, // TODO ADJUST
};
const paginationSelectedButtonBackgroundColors = {
  default: stylesConfig.colors.primaryAccentColorVariants.default, // TODO ADJUST
  hover: stylesConfig.colors.primaryAccentColorVariants.default, // TODO ADJUST
  active: stylesConfig.colors.primaryAccentColorVariants.dark, // TODO ADJUST
  nextback: globals.colors.gray, // TODO ADJUST
};

const buttonFontSize = stylesConfig.typography.fontSize(1);
const startOverButtonFontSize = stylesConfig.typography.fontSize(1);
const startOverButtonTextColor = stylesConfig.colors.primaryFontColor;

const outlineFocusSecondaryColor = stylesConfig.colors.secondary.spectrum400;

export const ButtonReset = {
  display: "inline-block",
  padding: 0,
  margin: 0,
  background: "transparent",
  border: 0,
  cursor: "pointer",
  fontFamily: "inherit",
  fontWeight: 700,
};

export const Button = {
  ...ButtonReset,
  position: "relative",
  padding: ["12px", "20px"],
  minWidth: "215px",
  borderRadius: "25px",
  backgroundColor: "white",
  border: (configuration: ResolvedComponentsConfiguration): string =>
    `1px solid ${previousButtonBorderColor(configuration)}`,
  fontSize: buttonFontSize,
  color: previousButtonTextColor,
  fontWeight: "700",
  transition: "background .3s ease",
  [mediaQuery.hover]: {
    "&:hover": {
      background: buttonBackgroundColors.previous.hover,
    },
  },
  "&:active": {
    background: buttonBackgroundColors.previous.active,
  },
  "&:focus": {
    outlineColor: outlineFocusSecondaryColor,
  },
  "&.is-hidden": {
    display: "none",
  },
  [`@media (${globals.breakpoints.$xs})`]: {
    minWidth: "45%",
  },
};

export const ButtonPrimary = {
  ...Button,
  background: buttonBackgroundColors.next.default,
  border: (configuration: ResolvedComponentsConfiguration): string =>
    `1px solid ${nextButtonBorderColor(configuration)}`,
  color: nextButtonTextColor,
  [mediaQuery.hover]: {
    "&:hover": {
      background: buttonBackgroundColors.next.hover,
    },
  },
  "&:active": {
    background: buttonBackgroundColors.next.active,
  },
};

export const ButtonPagination = {
  ...ButtonReset,
  minWidth: "30px",
  minHeight: "30px",
  padding: ["5px", 0],
  border: "1px solid",
  borderColor: paginationButtonBorderColor.default,
  background: paginationSelectedButtonBackgroundColors.default,
  color: globals.colors.white,
  marginRight: "10px",
  borderRadius: "25px",
  transition: "background .3s ease, border .3s ease",
  "&:last-child": {
    marginRight: 0,
  },
  [mediaQuery.hover]: {
    "&:hover": {
      background: paginationButtonBackgroundColors.hover,
    },
  },
  "&:active": {
    background: paginationButtonBackgroundColors.active,
  },
};
export const ButtonPaginationSelected = {
  ...ButtonPagination,
  background: paginationSelectedButtonBackgroundColors.active,
  border: "1px solid",
  borderColor: paginationButtonBorderColor.active,
  [mediaQuery.hover]: {
    "&:hover": {
      background: paginationSelectedButtonBackgroundColors.active,
    },
  },
  "&:active": {
    background: paginationSelectedButtonBackgroundColors.active,
  },
};

export const ButtonPaginationNextBack = {
  paddingLeft: "10px",
  paddingRight: "10px",
  fontWeight: 700,
  background: paginationSelectedButtonBackgroundColors.nextback,
  border: "1px solid",
  borderColor: paginationButtonBorderColor.nextback,
  color: paginationNextButtonTextColor,
  [mediaQuery.hover]: {
    "&:hover": {
      background: paginationSelectedButtonBackgroundColors.nextback,
    },
  },
};

export const ButtonStartOver = {
  ...ButtonReset,
  position: "relative",
  fontSize: startOverButtonFontSize,
  color: startOverButtonTextColor,
  display: "flex",
  flexFlow: "wrap row",
  alignItems: "center",
  justifyContent: "center",
  background: buttonBackgroundColors.startOver.default,
  padding: ["13px", "10px"],
  marginRight: "10px",
  borderRadius: "25px",
  minWidth: "25px",
  transition: "background .3s ease, border .3s ease",
};

export const ButtonNextInProgress = {
  ...Button,
  backgroundColor: stylesConfig.colors.secondary.spectrum800,
  borderColor: stylesConfig.colors.secondary.spectrum800,
  borderRadius: 0,
  color: stylesConfig.colors.secondary.spectrum800,
  marginRight: 0,
  transition: "none",
  [mediaQuery.hover]: {
    "&:hover": {
      backgroundColor: stylesConfig.colors.secondary.spectrum800,
      color: stylesConfig.colors.secondary.spectrum800,
    },
  },
  "&:active": {
    color: stylesConfig.colors.secondary.spectrum800,
    background: stylesConfig.colors.secondary.spectrum800,
  },
  "&:after": {
    top: "50%",
    left: "50%",
    content: "''",
    position: "absolute",
    width: "24px",
    height: "24px",
    borderStyle: "solid",
    borderWidth: "1px",
    borderLeftColor: (configuration: ResolvedComponentsConfiguration): string =>
      `${nextButtonTextColor(configuration)}`,
    borderRightColor: (configuration: ResolvedComponentsConfiguration): string =>
      `${nextButtonTextColor(configuration)}`,
    borderBottomColor: (configuration: ResolvedComponentsConfiguration): string =>
      `${nextButtonTextColor(configuration)}`,
    borderTopColor: "transparent",
    "border-radius": "50%",
    transform: "rotate(90deg)",
    animation: "$navigationSpinner 1s linear infinite",
  },
  [`@media (${globals.breakpoints.$xs})`]: {
    order: 2,
    width: "calc(50% - 5px)",
    paddingLeft: 0,
    paddingRight: 0,
    maxWidth: "215px",
  },
};
